// src/pages/webmaster/appointments/Audit/AppointmentsAudit.tsx
import React, { useEffect, useMemo, useState } from "react";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";

/* ===== Tipuri ===== */
type Row = {
  id: string;
  appointment_id: string | null;
  timestamp: string;
  action: "created" | "updated" | "status_updated" | "deleted" | "moved" | "copied" | string;
  action_label: string;
  actor_user_id: string | null;
  actor_role: string | null;
  actor_name: string | null;
  changes?: any;          // poate veni string (JSON) sau obiect
  prev_snapshot?: any;    // idem
  new_snapshot?: any;     // idem
};

type StatusItem = {
  key: string;
  value: string;
  type: "therapy" | "activity";
  activity_key?: string | null;
};

const PAGE_SIZE = 100;

/* ===== Helpers sigure pentru parse ===== */
const ensureObj = (x: any) => {
  if (!x) return null;
  if (typeof x === "string") {
    try { return JSON.parse(x); } catch { return null; }
  }
  if (typeof x === "object") return x;
  return null;
};

// Uneori backend poate returna { old: "...", new: "..." }.
// Alegem valoarea dorită.
const unwrap = (v: any, prefer: "old" | "new" = "new") => {
  if (v && typeof v === "object" && ("old" in v || "new" in v)) {
    return v[prefer] ?? v.old ?? v.new ?? "";
  }
  return v;
};

const pad = (n: number) => String(n).padStart(2, "0");
const isISO = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}T/.test(s);

const toDateObj = (iso?: string | null) => {
  if (!iso || !isISO(iso)) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const roDate = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${d.getFullYear()}`;
const roTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const roDateTime = (d: Date) => `${roDate(d)} ${roTime(d)}`;

const fmtDateAndInterval = (startISO?: string | null, endISO?: string | null) => {
  const s = toDateObj(startISO || undefined);
  const e = toDateObj(endISO || undefined);
  if (!s || !e) return "—";
  return `${roDate(s)} ${roTime(s)}–${roTime(e)}`;
};
const fmtInterval = (startISO?: string | null, endISO?: string | null) => {
  const s = toDateObj(startISO || undefined);
  const e = toDateObj(endISO || undefined);
  if (!s || !e) return "—";
  return `${roTime(s)}–${roTime(e)}`;
};

const same = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);
const joinParts = (arr: string[]) => arr.filter(Boolean).join("; ");

const typeLabel = (t?: string) => {
  switch (t) {
    case "therapy": return "Terapie";
    case "activity": return "Activitate";
    case "medical_activity": return "Activitate medicală";
    case "medical_consult": return "Consult medical";
    default: return t || "—";
  }
};

/* ===== Pagina ===== */
const AppointmentsAudit: React.FC = () => {
  const { token } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "true");

  // filtre
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [action, setAction] = useState<""|"created"|"updated"|"status_updated"|"deleted"|"moved"|"copied">("");

  // list + paginație
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const maxPage = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  /* ===== Mape pentru etichete prietenoase ===== */
  const [therapyMap, setTherapyMap] = useState<Record<string, string>>({});
  const [activityMap, setActivityMap] = useState<Record<string, string>>({});
  const [medActivityMap, setMedActivityMap] = useState<Record<string, string>>({});
  const [consultMap, setConsultMap] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [therapistNames, setTherapistNames] = useState<Record<string, string>>({});
  const [patientNames, setPatientNames] = useState<Record<string, string>>({});

  /* ===== Fetch meta (o singură dată) ===== */
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [
          thRes, actRes, stRes, medActRes, consRes, thUsersRes, medUsersRes, patientsRes
        ] = await Promise.all([
          fetch(`${API_BASE_URL}/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/appointment-status`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/medical-activities`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/types/consult-types`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/users/therapists`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/users/medics`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/patient/get-all`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const th = await thRes.json();
        const ac = await actRes.json();
        const st = await stRes.json();

        const tMap: Record<string, string> = {};
        (th?.data || []).forEach((x: any) => { tMap[x.key] = x.value; });
        setTherapyMap(tMap);

        const aMap: Record<string, string> = {};
        (ac?.data || []).forEach((x: any) => { aMap[x.key] = x.value; });
        setActivityMap(aMap);

        setStatuses(st?.data || []);

        if (medActRes) {
          const ma = await medActRes.json();
          const mMap: Record<string, string> = {};
          (ma?.data || []).forEach((x: any) => { mMap[x.key] = x.value; });
          setMedActivityMap(mMap);
        }
        if (consRes) {
          const co = await consRes.json();
          const cMap: Record<string, string> = {};
          (co?.data || []).forEach((x: any) => { cMap[x.key] = x.value; });
          setConsultMap(cMap);
        }

        const thUsers = await thUsersRes.json();
        const medUsers = await medUsersRes.json();
        const nameMap: Record<string, string> = {};
        (Array.isArray(thUsers) ? thUsers : thUsers?.data || []).forEach((u: any) => {
          nameMap[u.user_id] = `${u.firstname} ${u.lastname}`.trim();
        });
        (Array.isArray(medUsers) ? medUsers : medUsers?.data || []).forEach((u: any) => {
          nameMap[u.user_id] = `${u.firstname} ${u.lastname}`.trim();
        });
        setTherapistNames(nameMap);

        const pats = await patientsRes.json();
        const pMap: Record<string, string> = {};
        (Array.isArray(pats) ? pats : pats?.data || pats || []).forEach((p: any) => {
          pMap[String(p.id)] = `${p.firstname} ${p.lastname}`.trim();
        });
        setPatientNames(pMap);
      } catch (e) {
        console.warn("Nu am putut încărca meta pentru istoric:", e);
      }
    })();
  }, [token]);

  /* ===== Fetch listă ===== */
  const fetchData = async (opts?: { resetPage?: boolean }) => {
    const currentPage = opts?.resetPage ? 1 : page;
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (action) params.set("action", action);
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String((currentPage - 1) * PAGE_SIZE));

    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/admin/appointments/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setRows(data.data || []);
      setTotal(Number(data.total || 0));
      if (opts?.resetPage) setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchData({ resetPage: true }); }, [token]);
  useEffect(() => { if (token) fetchData(); /* eslint-disable-next-line */ }, [page]);

  const fromIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toIdx = Math.min(page * PAGE_SIZE, total);

  const goFirst = () => setPage(1);
  const goPrev  = () => setPage(p => Math.max(1, p - 1));
  const goNext  = () => setPage(p => Math.min(maxPage, p + 1));
  const goLast  = () => setPage(maxPage);

  /* ===== Getters pentru etichete din snapshot (cu unwrap) ===== */
  const mapDesc = (t?: string, code?: string) => {
    if (!code) return "";
    switch (t) {
      case "therapy":          return therapyMap[code]     || code;
      case "activity":         return activityMap[code]    || code;
      case "medical_activity": return medActivityMap[code] || code;
      case "medical_consult":  return consultMap[code]     || code;
      default:                 return code;
    }
  };

  const therapistLabel = (idOrName?: string) => {
    if (!idOrName) return "—";
    // dacă e deja nume uman (are spațiu), îl folosim
    if (/\s/.test(idOrName)) return idOrName;
    return therapistNames[idOrName] || idOrName;
  };

  const patientLabel = (idOrName?: string) => {
    if (idOrName == null || idOrName === "") return "";
    if (/\s/.test(String(idOrName))) return String(idOrName);
    return patientNames[String(idOrName)] || String(idOrName);
  };

  const statusLabel = (key?: string, snap?: any): string => {
    if (!key) return "—";
    const t = snap?.type;
    const descKey = snap?.appointment_description;
    if (t === "therapy") {
      const hit = statuses.find(s => s.key === key && s.type === "therapy");
      return hit?.value || key;
    } else {
      const hit = statuses.find(s => s.key === key && s.type === "activity" && (s.activity_key == null || s.activity_key === descKey));
      return hit?.value || key;
    }
  };

  // Construiește descrierea: "Terapie Balance-Suit pentru Ion Popescu"
  const basicInfo = (snap: any, prefer: "old" | "new" = "new") => {
    if (!snap) return "";
    const tRaw  = unwrap(snap.type, prefer);
    const dRaw  = unwrap(snap.appointment_description, prefer);
    const thRaw = unwrap(snap.therapist_id, prefer);
    const pRaw  = unwrap(snap.patient_id, prefer);

    const t = typeLabel(String(tRaw || ""));
    const d = mapDesc(String(tRaw || ""), dRaw ? String(dRaw) : "");
    const p = patientLabel(pRaw ? String(pRaw) : "");

    return `${t} ${d}${p ? ` pentru ${p}` : ""}`.trim();
  };

  const dateAndInterval = (snap: any, prefer: "old" | "new" = "new") => {
    const s = unwrap(snap?.start_time, prefer);
    const e = unwrap(snap?.end_time, prefer);
    return fmtDateAndInterval(s, e);
  };
  const onlyInterval = (snap: any, prefer: "old" | "new" = "new") => {
    const s = unwrap(snap?.start_time, prefer);
    const e = unwrap(snap?.end_time, prefer);
    return fmtInterval(s, e);
  };

  /* ===== RENDER DETALII ===== */
  const renderCreated = (cur: any) => {
    const parts = [
      `Creată: ${basicInfo(cur, "new")}`.trim(),
      `Data/Interval: ${dateAndInterval(cur, "new")}`,
      `Terapeut: ${therapistLabel(String(unwrap(cur?.therapist_id, "new") || ""))}`,
    ];
    const st = unwrap(cur?.status, "new");
    const stLabel = statusLabel(st ? String(st) : undefined, { type: unwrap(cur?.type,"new"), appointment_description: unwrap(cur?.appointment_description,"new") });
    if (stLabel && stLabel !== "—") parts.push(`Status: ${stLabel}`);
    return joinParts(parts);
  };

  const renderDeleted = (prev: any) => {
    const parts = [
      `Ștearsă: ${basicInfo(prev, "old")}`.trim(),
      `Data/Interval: ${dateAndInterval(prev, "old")}`,
      `Terapeut: ${therapistLabel(String(unwrap(prev?.therapist_id, "old") || ""))}`,
    ];
    const st = unwrap(prev?.status, "old");
    const stLabel = statusLabel(st ? String(st) : undefined, { type: unwrap(prev?.type,"old"), appointment_description: unwrap(prev?.appointment_description,"old") });
    if (stLabel && stLabel !== "—") parts.push(`Status înainte: ${stLabel}`);
    return joinParts(parts);
  };

  const renderStatusUpdated = (prev: any, cur: any) => {
    const base = cur || prev;
    const parts = [
      `Status modificat: ${basicInfo(base, cur ? "new" : "old")}`,
      `Data/Interval: ${dateAndInterval(base, cur ? "new" : "old")}`,
    ];
    const oldSt = unwrap(prev?.status, "old");
    const newSt = unwrap(cur?.status, "new");
    const oldLabel = statusLabel(oldSt ? String(oldSt) : undefined, { type: unwrap(prev?.type,"old"), appointment_description: unwrap(prev?.appointment_description,"old") });
    const newLabel = statusLabel(newSt ? String(newSt) : undefined, { type: unwrap(cur?.type,"new"), appointment_description: unwrap(cur?.appointment_description,"new") });
    if (oldLabel || newLabel) parts.push(`Status: ${oldLabel || "—"} → ${newLabel || "—"}`);
    return joinParts(parts);
  };

  const renderUpdated = (prev: any, cur: any, isCopied = false) => {
    const head = isCopied
      ? `Creată (copiată): ${basicInfo(cur, "new")}`
      : `Modificată: ${basicInfo(cur, "new")}`;

    const prevFull = dateAndInterval(prev, "old");
    const nextFull = dateAndInterval(cur, "new");

    const bullets: string[] = [];

    // schimbare interval?
    const prevS = unwrap(prev?.start_time, "old");
    const prevE = unwrap(prev?.end_time, "old");
    const curS  = unwrap(cur?.start_time, "new");
    const curE  = unwrap(cur?.end_time, "new");
    const intervalChanged = fmtDateAndInterval(prevS, prevE) !== fmtDateAndInterval(curS, curE);

    if (isCopied) {
      // arătăm întotdeauna de când → când s-a copiat
      bullets.push(`Din ${prevFull} în ${nextFull}`);
    } else if (intervalChanged) {
      bullets.push(`Interval: de la ${prevFull} la ${nextFull}`);
    } else {
      // context
      bullets.push(`Data/Interval: ${nextFull}`);
    }

    // terapuet/pacient/tip/descriere/status
    const prevTh = therapistLabel(String(unwrap(prev?.therapist_id,"old") || ""));
    const curTh  = therapistLabel(String(unwrap(cur?.therapist_id,"new")  || ""));
    if (prevTh !== curTh) bullets.push(`Terapeut: ${prevTh} → ${curTh}`);

    const prevP = patientLabel(String(unwrap(prev?.patient_id,"old") || ""));
    const curP  = patientLabel(String(unwrap(cur?.patient_id,"new")  || ""));
    if (prevP !== curP) bullets.push(`Pacient: ${prevP || "—"} → ${curP || "—"}`);

    const prevType = String(unwrap(prev?.type,"old") || "");
    const curType  = String(unwrap(cur?.type,"new")  || "");
    const prevDesc = mapDesc(prevType, String(unwrap(prev?.appointment_description,"old") || ""));
    const curDesc  = mapDesc(curType,  String(unwrap(cur?.appointment_description,"new")  || ""));
    if (prevType !== curType || prevDesc !== curDesc) {
      bullets.push(`Programare: ${typeLabel(prevType)} ${prevDesc} → ${typeLabel(curType)} ${curDesc}`);
    }

    const prevSt = unwrap(prev?.status,"old"); const curSt = unwrap(cur?.status,"new");
    const prevStL = statusLabel(prevSt ? String(prevSt) : undefined, { type: prevType, appointment_description: unwrap(prev?.appointment_description,"old") });
    const curStL  = statusLabel(curSt ? String(curSt) : undefined,  { type: curType,  appointment_description: unwrap(cur?.appointment_description,"new") });
    if (prevStL !== curStL) bullets.push(`Status: ${prevStL} → ${curStL}`);

    return joinParts([head, ...bullets]);
  };

  const renderDetails = (r: Row) => {
    const prev = ensureObj(r.prev_snapshot);
    const cur  = ensureObj(r.new_snapshot);

    switch (r.action) {
      case "created":
        return renderCreated(cur);
      case "deleted":
        return renderDeleted(prev);
      case "status_updated":
        return renderStatusUpdated(prev, cur);
      case "copied":
        return renderUpdated(prev, cur, true);
      case "updated":
      case "moved":
      default:
        return renderUpdated(prev, cur, false);
    }
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={setIsCollapsed} />
      <div className="page-content">
        <h1 className="page-title">Setări → Istoric programări</h1>

        {/* Filtre */}
        <div className="filters-row">
          <label>De la</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <label>Până la</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          <label>Acțiune</label>
          <select value={action} onChange={e => setAction(e.target.value as any)}>
            <option value="">Toate</option>
            <option value="created">Creată</option>
            <option value="updated">Modificată</option>
            <option value="status_updated">Status modificat</option>
            <option value="moved">Mutată</option>
            <option value="deleted">Ștearsă</option>
            <option value="copied">Copiată</option>
          </select>
          <button className="primary-btn" onClick={() => fetchData({ resetPage: true })} disabled={loading}>
            {loading ? "Caut…" : "Caută"}
          </button>
        </div>

        {/* Tabel */}
        {loading ? (
          <p>Se încarcă…</p>
        ) : (
          <>
            <div className="table-rounded-container">
              <table className="table-rounded history-table">
                <thead>
                  <tr>
                    <th style={{ width: 180 }}>Dată</th>
                    <th style={{ width: 160 }}>Acțiune</th>
                    <th style={{ width: 280 }}>Utilizator</th>
                    <th>Detalii</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{new Date(r.timestamp).toLocaleString("ro-RO")}</td>
                      <td>{r.action_label || r.action}</td>
                      <td>
                        {r.actor_name || r.actor_user_id || "—"}
                        {r.actor_role ? ` (${r.actor_role})` : ""}
                      </td>
                      <td><div className="audit-details">{renderDetails(r)}</div></td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={4}>Nimic găsit.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginare */}
            <div className="pagination-bar">
              <div style={{ color: "#666" }}>
                Afișez {fromIdx}-{toIdx} din {total} (câte {PAGE_SIZE}/pagină)
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="secondary-btn" onClick={goFirst} disabled={page<=1}>Prima</button>
                <button className="secondary-btn" onClick={goPrev}  disabled={page<=1}>Înapoi</button>
                <span style={{ alignSelf: "center" }}>Pagina {page} / {maxPage}</span>
                <button className="secondary-btn" onClick={goNext}  disabled={page>=maxPage}>Înainte</button>
                <button className="secondary-btn" onClick={goLast}  disabled={page>=maxPage}>Ultima</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppointmentsAudit;